import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  KeyRound,
  Loader2,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  Trash2,
  PlugZap,
  Lock,
} from "lucide-react";

function StatusBadge({ provider }) {
  if (!provider?.configured) {
    return (
      <Badge
        data-testid="connection-status-badge"
        className="gap-1 rounded-full border-0 bg-slate-800/70 px-3 py-1 text-[10px] uppercase tracking-wider text-slate-400 ring-1 ring-border"
      >
        Not configured
      </Badge>
    );
  }
  const ok = provider.last_test_status === "ok";
  return (
    <Badge
      data-testid="connection-status-badge"
      className={`gap-1 rounded-full border-0 px-3 py-1 text-[10px] uppercase tracking-wider ring-1 ${
        ok
          ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
          : "bg-red-500/15 text-red-300 ring-red-500/30"
      }`}
    >
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {ok ? "Connected" : "Failed"}
    </Badge>
  );
}

export function SettingsView() {
  const [provider, setProvider] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [voiceId, setVoiceId] = useState("");
  const [savingVoice, setSavingVoice] = useState(false);

  const load = () => {
    api.get("/settings/provider").then((r) => setProvider(r.data)).catch(() => setProvider({ configured: false }));
  };

  const updateVoice = async () => {
    const vid = voiceId.trim();
    if (!vid) { toast.error("Enter an ElevenLabs Voice ID."); return; }
    setSavingVoice(true);
    try {
      const { data } = await api.put("/narrator-preset/voice", { voice_id: vid });
      toast.success(`Narrator voice updated to "${data.narrator_preset.name}". Applies to new generations.`);
      setVoiceId("");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSavingVoice(false); }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (apiKey.trim().length < 8) {
      toast.error("Enter a valid ElevenLabs API key.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/provider", { api_key: apiKey.trim() });
      setProvider(data);
      setApiKey("");
      if (data.last_test_status === "ok") toast.success("Key stored and connection verified.");
      else toast.error(data.last_test_message || "Key stored but connection failed.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/provider/test");
      setProvider(data);
      if (data.last_test_status === "ok") toast.success("Connection healthy.");
      else toast.error(data.last_test_message || "Connection failed.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete("/settings/provider");
      toast.success("Provider secret deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const preset = provider?.narrator_preset || {};

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">System</div>
        <h1 className="font-serif text-4xl tracking-tight text-slate-100">Workstation settings</h1>
        <p className="mt-2 text-sm text-slate-400">Provider credentials and locked studio defaults.</p>
      </div>

      {/* Provider secret */}
      <div className="mb-8 rounded-xl border border-amber-500/20 bg-[#12161f] p-6">
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PlugZap className="h-5 w-5 text-amber-400" />
            <h2 className="font-serif text-2xl text-slate-100">ElevenLabs connection</h2>
          </div>
          <StatusBadge provider={provider} />
        </div>
        <p className="mb-5 flex items-center gap-1.5 text-xs text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
          Stored encrypted server-side. The key is never sent to the browser, logs, or exports.
        </p>

        {provider?.configured && (
          <div className="mb-5 rounded-lg border border-border bg-slate-950/40 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Stored key</div>
                <div data-testid="stored-key-hint" className="mt-1 font-mono text-sm text-slate-200">
                  {provider.key_hint}
                </div>
              </div>
              {provider.subscription?.tier && (
                <div className="text-right">
                  <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Tier</div>
                  <div className="mt-1 font-mono text-sm text-amber-200">{provider.subscription.tier}</div>
                </div>
              )}
            </div>
            {provider.subscription?.character_limit != null && (
              <div className="mt-3 font-mono text-xs text-slate-500">
                Usage: {(provider.subscription.character_count ?? 0).toLocaleString()} /{" "}
                {provider.subscription.character_limit.toLocaleString()} characters
              </div>
            )}
            {provider.last_test_message && (
              <div className="mt-2 text-xs text-slate-500">{provider.last_test_message}</div>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="api-key" className="text-xs uppercase tracking-wider text-slate-400">
            {provider?.configured ? "Replace API key" : "ElevenLabs API key"}
          </Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="api-key"
                type="password"
                data-testid="elevenlabs-api-key-input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk_..."
                autoComplete="off"
                className="bg-slate-950/60 pl-9 font-mono"
              />
            </div>
            <Button
              data-testid="save-key-button"
              onClick={save}
              disabled={saving}
              className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save & verify
            </Button>
          </div>
          <p className="pt-1 text-xs text-slate-500">
            Get a restricted key at{" "}
            <span className="font-mono text-slate-400">elevenlabs.io/app/settings/api-keys</span>
          </p>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <Button
            data-testid="test-connection-button"
            variant="outline"
            onClick={test}
            disabled={testing || !provider?.configured}
            className="gap-2 border-amber-500/30 text-amber-200 hover:bg-amber-500/10 hover:text-amber-100"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
            Test connection
          </Button>

          {provider?.configured && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="delete-key-button"
                  variant="ghost"
                  className="gap-2 text-slate-400 hover:bg-red-500/10 hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" /> Remove key
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="border-border bg-[#12161f] text-slate-100">
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove the ElevenLabs key?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    The encrypted secret will be deleted from the server. You can add a new key at any time.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-border bg-transparent text-slate-300">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={remove}
                    data-testid="confirm-delete-key"
                    className="bg-red-600 text-white hover:bg-red-500"
                  >
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Locked defaults */}
      <div className="rounded-xl border border-border bg-[#12161f] p-6">
        <div className="mb-4 flex items-center gap-2">
          <Lock className="h-4 w-4 text-slate-500" />
          <h2 className="font-serif text-2xl text-slate-100">Narrator voice &amp; settings</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <div className="col-span-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] px-4 py-3 md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-500/70">
              {preset.name || "Narrator"} · Voice ID
            </div>
            <div data-testid="settings-narrator-voice-id" className="mt-1 select-all font-mono text-sm text-amber-200">
              {preset.voice_id}
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Input
                data-testid="narrator-voice-input"
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                placeholder="Paste a new ElevenLabs Voice ID to change the narrator"
                className="bg-slate-950/60 font-mono text-sm"
              />
              <Button
                data-testid="narrator-voice-save"
                onClick={updateVoice}
                disabled={savingVoice || !provider?.configured}
                className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-50"
              >
                {savingVoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Update voice
              </Button>
            </div>
            <div className="mt-1.5 text-[11px] text-slate-500">
              {provider?.configured
                ? "The ID is validated with ElevenLabs and its real name is pulled. Applies to NEW generations — existing takes keep their voice until regenerated."
                : "Configure your ElevenLabs API key above before changing the narrator voice."}
            </div>
          </div>
          {[
            ["Model", preset.model],
            ["Speed", preset.speed?.toFixed(1)],
            ["Stability", preset.stability != null ? `${Math.round(preset.stability * 100)}%` : ""],
            ["Similarity", preset.similarity != null ? `${Math.round(preset.similarity * 100)}%` : ""],
            ["Speaker Boost", preset.speaker_boost ? "On" : "Off"],
            ["Review Output", preset.output],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-border bg-slate-950/40 px-4 py-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{k}</div>
              <div className="mt-1 font-mono text-sm text-slate-200">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
